import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useSidebar } from 'src/components/ui/sidebar';

import type { TourDefinition, TourStep } from './types';
import { TOUR_DETAIL_PATH_ATTR } from './tour-anchors';
import { getTourState, setTourState, subscribeTourState, type TourState } from './tour-store';
import { setTourRecord, type TourStatus } from './tour-storage';
import {
  disposeDemoSession,
  ensureDemoClient,
  hasDemoFailed,
  subscribeDemoSession,
  getDemoSnapshot,
} from './demo/demo-session';
import { TourOverlay } from './tour-overlay';

// How long to wait for an anchor before the step settles into a centered card
// for its remainder. Covers lazy-loaded routes and data-loading skeletons. The
// tour NEVER advances on its own — a missing anchor degrades the presentation
// (centered card instead of spotlight), it does not move the step.
const ANCHOR_TIMEOUT_MS = 4000;
// How long a previously resolved anchor may be absent (a refetch unmounting a
// list, a skeleton swap) before the spotlight falls back to the centered card.
const VANISH_GRACE_MS = 1000;
// On a step change the *previous* step's target is kept this long while the new
// anchor is polled for, so a same-page anchored→anchored transition morphs the
// spotlight continuously instead of flashing a bare scrim. Past the window with
// no new anchor, the carried target is dropped (hole closes, card glides to
// center already showing the new step); a late anchor still reopens it (D3).
const HANDOFF_MS = 350;
const POLL_INTERVAL_MS = 100;

function prefersReducedMotion(): boolean {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}

// Bring the spotlight target into view, but only when it isn't already: a short
// target fully inside the [80px, vh−80px] band, or a taller-than-viewport target
// already spanning that band, needs no scroll — scrolling a visible anchor anyway
// just yanks the page. The `block` rule (start for page-height targets so their
// *top* leads rather than their middle, else center) is kept from v2; reduced
// motion drops the smooth behavior so the two motion systems never fight (D7).
function scrollTargetIntoView(el: HTMLElement) {
  const rect = el.getBoundingClientRect();
  const vh = window.innerHeight;
  const bandTop = 80;
  const bandBottom = vh - 80;
  const fitsInBand = rect.top >= bandTop && rect.bottom <= bandBottom;
  const coversBand = rect.top <= bandTop && rect.bottom >= bandBottom;
  if (fitsInBand || coversBand) return;
  const block = rect.height > vh * 0.7 ? 'start' : 'center';
  el.scrollIntoView({ block, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
}

// A step marked `skipIfAbsent` is dropped at user-navigation time when its
// surface is not on the page (conditionally rendered, e.g. ActivationLadderCard
// retires at the top activation level — teaching an invisible card from the
// centered fallback was ruled bad UX). The check runs synchronously inside
// next()/back() — never from a timer — so the no-self-advance invariant holds.
// It can only probe the page the user is on: a candidate whose route differs
// from the current pathname is never queried. That is why skipped indices are
// also recorded in the store for the rest of the run (see TourState.skipped):
// stepping backward across a route boundary must skip the same steps without
// being able to query the page they live on.
function isAbsentOnCurrentPage(step: TourStep): boolean {
  return Boolean(
    step.skipIfAbsent &&
    step.anchor != null &&
    (!step.route || step.route === window.location.pathname) &&
    !document.querySelector(`[data-tour="${step.anchor}"]`)
  );
}

// Adaptive detail steps (v3.4). Landing on a step with `detail` probes the
// current page — synchronously, at click time, same family as skipIfAbsent —
// for the detail route the source list element publishes (its first entity).
// A hit is recorded per run; a miss leaves the step on its authored fallback.
function resolveDetail(
  tour: TourDefinition,
  index: number,
  resolved: Record<number, string>
): Record<number, string> {
  const target = tour.steps[index];
  if (!target?.detail || resolved[index] !== undefined) return resolved;
  // A list rendering demo data publishes a detail path for a synthetic entity.
  // Following it into a step that runs on the real client would navigate the
  // merchant to an id their store does not have — worse than the authored
  // illustrated fallback this step already carries.
  //
  // Gated on the CURRENT page being demo-engaged, not on a demo client merely
  // existing: the client lives for the whole run, so testing existence would
  // disable the real-data detail upgrade on every later step once the tour had
  // passed through a demo one.
  if (isDemoEngaged() && !target.demo) return resolved;
  const source = document.querySelector<HTMLElement>(`[data-tour="${target.detail.source}"]`);
  const path = source?.getAttribute(TOUR_DETAIL_PATH_ATTR);
  return path ? { ...resolved, [index]: path } : resolved;
}

// Whether the page the browser is on right now is being served demo data — the
// same derivation the boundary makes, for callers outside React.
function isDemoEngaged(): boolean {
  if (getDemoSnapshot() === null) return false;
  return getTourState()?.demoRoutes.includes(window.location.pathname) ?? false;
}

// Every route this run may render demo data on. Collected for the whole run
// rather than per step, so the set does not change as the tour moves between two
// demo steps on different pages. That stability is the point: it is what removes
// the list→detail handoff window, where a per-step route would flip before the
// router committed and strip the demo client and label off a page still painting
// fixture rows.
function collectDemoRoutes(tour: TourDefinition, resolvedPaths: Record<number, string>): string[] {
  const routes = new Set<string>();
  tour.steps.forEach((step, index) => {
    if (!step.demo) return;
    if (step.route) routes.add(step.route);
    const resolved = resolvedPaths[index];
    if (resolved) routes.add(resolved);
  });
  return [...routes];
}

// With a resolved path the step upgrades onto the real detail page: navigate
// there and spotlight the detail anchor. The fallback illustration is kept but
// demoted to centered presentations only — while the real page loads (and if its
// anchor never mounts: slow fetch, entity dead on re-entry) the card still
// teaches with the diagram instead of degrading to bare title+body, and it yields
// to the real spotlight the moment the anchor opens. Without a resolved path the
// step runs as authored.
function effectiveStep(
  step: TourStep,
  index: number,
  resolvedPaths: Record<number, string>
): TourStep {
  // A demo step whose fixtures failed has nothing to spotlight: its anchor
  // still mounts (an empty list is a list), so without this the tour would
  // highlight the real empty surface the demo exists to replace. Dropping the
  // anchor centers the card on the illustration — the authored fallback.
  if (step.demo && hasDemoFailed()) {
    return { ...step, anchor: null, route: undefined };
  }
  const path = step.detail ? resolvedPaths[index] : undefined;
  if (!path || !step.detail) return step;
  return { ...step, route: path, anchor: step.detail.anchor, illustrationOnlyWhenCentered: true };
}

interface TourContextValue {
  active: boolean;
  step: TourStep | null;
  stepIndex: number;
  stepCount: number;
  startTour: (tourId: string) => void;
  stopTour: (reason: TourStatus) => void;
  next: () => void;
  back: () => void;
}

const TourContext = createContext<TourContextValue | null>(null);

export function useTour(): TourContextValue {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within a TourProvider.');
  }
  return context;
}

interface TourProviderProps {
  children: React.ReactNode;
  tours?: TourDefinition[];
}

function findTour(tours: TourDefinition[], state: TourState | null): TourDefinition | null {
  if (!state) return null;
  return tours.find((tour) => tour.id === state.activeTourId) ?? null;
}

export function TourProvider({ children, tours = [] }: TourProviderProps) {
  const state = useSyncExternalStore(subscribeTourState, getTourState);
  // Subscribed so a fixture failure re-renders: effectiveStep's demotion to the
  // illustrated card is derived from the demo phase, and without this the
  // provider would keep showing a spotlight on the real, empty page.
  // Retained, not discarded: the redirect effect below must re-run when a
  // runtime handler failure nulls the client, and that is the only dependency
  // that changes in that case.
  const demoClient = useSyncExternalStore(subscribeDemoSession, getDemoSnapshot);
  const location = useLocation();
  const navigate = useNavigate();
  const { setOpen, isMobile } = useSidebar();

  const [targetEl, setTargetEl] = useState<HTMLElement | null>(null);

  // Ref read by the polling interval and the mutation callbacks, so neither
  // needs `tours` in its dependency list. Travel direction and the
  // navigated-for-step marker live in the module store, NOT in refs: the
  // provider remounts on every cross-route navigation, and a ref would reset at
  // exactly the moment those guards matter.
  const toursRef = useRef(tours);
  useEffect(() => {
    toursRef.current = tours;
  }, [tours]);

  const activeTour = findTour(tours, state);
  const rawCurrentStep = activeTour && state ? (activeTour.steps[state.stepIndex] ?? null) : null;
  const currentStep =
    rawCurrentStep && state
      ? effectiveStep(rawCurrentStep, state.stepIndex, state.resolvedPaths)
      : null;
  const stepCount = activeTour ? activeTour.steps.length : 0;
  const active = Boolean(currentStep);

  // A fixture failure while on a resolved detail path strands the browser on a
  // URL that exists only in the demo — the real client resolves it to "not
  // found" under the step's illustrated card. The step has already demoted, so
  // this returns the browser to the step's authored (real) route. `replace` so
  // Back doesn't walk into the dead URL again.
  const failedStepIndex = state?.stepIndex;
  const failedResolvedPath = failedStepIndex != null ? state?.resolvedPaths[failedStepIndex] : null;
  useEffect(() => {
    if (!hasDemoFailed() || !activeTour || failedStepIndex == null) return;
    const failedStep = activeTour.steps[failedStepIndex];
    // Only a DEMO step's resolved path is synthetic. The failure flag lives for
    // the whole run, so without this a later real-data step — whose resolved
    // path is a genuine product of the merchant's — would be redirected away
    // from the very page the v3.4 upgrade exists to show.
    if (!failedStep?.demo) return;
    if (!failedResolvedPath || failedResolvedPath !== location.pathname) return;
    const authored = failedStep.route;
    if (authored && authored !== location.pathname) navigate(authored, { replace: true });
  }, [activeTour, demoClient, failedStepIndex, failedResolvedPath, location.pathname, navigate]);

  const startTour = useCallback((tourId: string) => {
    const tour = toursRef.current.find((candidate) => candidate.id === tourId);
    setTourState({
      activeTourId: tourId,
      stepIndex: 0,
      navigatedFor: null,
      skipped: [],
      resolvedPaths: {},
      demoRoutes: tour ? collectDemoRoutes(tour, {}) : [],
    });
  }, []);

  // Only job left: make sure a client exists before a demo step becomes current.
  // Nothing here decides which page renders demo data — the boundary derives that
  // from the committed location and the run's demo routes.
  const prepareDemo = useCallback(async (step: TourStep | null) => {
    if (!step?.demo) return 'ready' as const;
    return ensureDemoClient();
  }, []);

  const stopTour = useCallback(
    (reason: TourStatus) => {
      const cur = getTourState();
      if (cur) {
        const tour = findTour(toursRef.current, cur);
        setTourRecord(cur.activeTourId, reason, tour?.version ?? 0);
        // Ending the tour clears demoRoutes, so the boundary disengages at once
        // and a skipped tour never leaves synthetic rows clickable. The one case
        // that needs help is a RESOLVED DETAIL path: that URL exists only in the
        // demo, so the real client would render "not found" for an id the store
        // never had. Authored routes are always real, so returning to this step's
        // own route is safe.
        const resolved = cur.resolvedPaths[cur.stepIndex];
        if (resolved && resolved === window.location.pathname) {
          const authored = tour?.steps[cur.stepIndex]?.route;
          if (authored) navigate(authored, { replace: true });
        }
      }
      setTourState(null);
      disposeDemoSession();
    },
    [navigate]
  );

  const next = useCallback(async () => {
    const cur = getTourState();
    if (!cur) return;
    const tour = findTour(toursRef.current, cur);
    if (!tour) return;
    const skipped = new Set(cur.skipped);
    let nextIndex = cur.stepIndex + 1;
    while (nextIndex < tour.steps.length) {
      if (!skipped.has(nextIndex) && !isAbsentOnCurrentPage(tour.steps[nextIndex])) break;
      skipped.add(nextIndex);
      nextIndex += 1;
    }
    if (nextIndex >= tour.steps.length) {
      setTourRecord(cur.activeTourId, 'completed', tour.version);
      setTourState(null);
      disposeDemoSession();
      return;
    }
    // Detail resolution reads the current page's DOM and must stay synchronous
    // at click time — preparing the demo session below yields, and a read after
    // that await could race the route the previous step navigated to.
    const resolvedPaths = resolveDetail(tour, nextIndex, cur.resolvedPaths);
    const target = effectiveStep(tour.steps[nextIndex], nextIndex, resolvedPaths);
    const outcome = await prepareDemo(target);
    // Only supersession blocks the move: the run was disposed while the fixtures
    // loaded. A FAILED build must still advance, or the tour strands on the
    // previous step instead of reaching its illustrated fallback.
    if (outcome === 'superseded' || getTourState() !== cur) return;
    setTourState({
      activeTourId: cur.activeTourId,
      stepIndex: nextIndex,
      navigatedFor: null,
      skipped: [...skipped],
      resolvedPaths,
      demoRoutes: collectDemoRoutes(tour, resolvedPaths),
    });
  }, [prepareDemo]);

  const back = useCallback(async () => {
    const cur = getTourState();
    if (!cur) return;
    const tour = findTour(toursRef.current, cur);
    if (!tour) return;
    const skipped = new Set(cur.skipped);
    let prevIndex = cur.stepIndex - 1;
    while (prevIndex >= 0) {
      if (!skipped.has(prevIndex) && !isAbsentOnCurrentPage(tour.steps[prevIndex])) break;
      skipped.add(prevIndex);
      prevIndex -= 1;
    }
    if (prevIndex < 0) return;
    const resolvedPaths = resolveDetail(tour, prevIndex, cur.resolvedPaths);
    const target = effectiveStep(tour.steps[prevIndex], prevIndex, resolvedPaths);
    const outcome = await prepareDemo(target);
    if (outcome === 'superseded' || getTourState() !== cur) return;
    setTourState({
      activeTourId: cur.activeTourId,
      stepIndex: prevIndex,
      navigatedFor: null,
      skipped: [...skipped],
      resolvedPaths,
      demoRoutes: collectDemoRoutes(tour, resolvedPaths),
    });
  }, [prepareDemo]);

  // Resolve the current step's anchor: navigate to its route (at most once per
  // step, so a redirecting route can't loop), expand the sidebar for `nav:`
  // anchors, then poll for the element. Keyed on the store's primitive step
  // identity — not the step object — so an unstable `tours` prop can't reset the
  // poll clock on every parent render; the step is read live from the store.
  useEffect(() => {
    const cur = getTourState();
    if (!cur) {
      setTargetEl(null);
      return undefined;
    }

    const tour = findTour(toursRef.current, cur);
    const rawStep = tour ? (tour.steps[cur.stepIndex] ?? null) : null;
    const step = rawStep ? effectiveStep(rawStep, cur.stepIndex, cur.resolvedPaths) : null;
    if (!step) {
      setTargetEl(null);
      return undefined;
    }

    const stepKey = `${cur.activeTourId}:${cur.stepIndex}`;

    // Navigate at most once per step — the marker lives in the store so the
    // cross-route remount (or a redirecting route) can't re-fire it. After that
    // single attempt we always poll, regardless of pathname match, so a route
    // that redirects elsewhere still resolves at its real destination (or times
    // out and skips) instead of looping navigation or wedging.
    if (step.route && step.route !== location.pathname && cur.navigatedFor !== stepKey) {
      setTourState({ ...cur, navigatedFor: stepKey });
      setTargetEl(null);
      navigate(step.route);
      return undefined;
    }

    if (step.anchor == null) {
      setTargetEl(null);
      return undefined;
    }

    if (step.anchor.startsWith('nav:') && !isMobile) {
      setOpen(true);
    }

    let cancelled = false;
    const selector = `[data-tour="${step.anchor}"]`;

    // The poll runs while the step is live: anchors can change element identity
    // (loading skeleton swapped for the real header) or unmount mid-step
    // (activation ladder reaching its top level, a list refetch). Absence never
    // advances the step — after the timeout the step settles into a centered
    // card (targetEl null, poll stopped). Stopping the poll also stops late
    // upgrades that would yank the card into a spotlight mid-read.
    let lastSeenAt = Date.now();
    const stepStartedAt = Date.now();
    let currentEl: HTMLElement | null = null;
    // Whether the previous step's carried target has already been dropped for
    // this step (the handoff window expired unresolved). Guards the drop to once.
    let handoffDone = false;

    const timer = window.setInterval(() => {
      if (cancelled) return;
      const el = document.querySelector<HTMLElement>(selector);
      if (el) {
        lastSeenAt = Date.now();
        if (el !== currentEl) {
          currentEl = el;
          scrollTargetIntoView(el);
          setTargetEl(el);
        }
      } else if (Date.now() - lastSeenAt > ANCHOR_TIMEOUT_MS) {
        window.clearInterval(timer);
        currentEl = null;
        setTargetEl(null);
      } else if (currentEl && Date.now() - lastSeenAt > VANISH_GRACE_MS) {
        // Resolved anchor gone past the grace (not just a skeleton swap between
        // ticks): drop to the centered card while the poll keeps trying.
        currentEl = null;
        setTargetEl(null);
      } else if (!currentEl && !handoffDone && Date.now() - stepStartedAt > HANDOFF_MS) {
        // The new step's anchor hasn't resolved within the handoff window: drop
        // the previous step's carried target so the hole closes and the card
        // glides to center. Keep polling — a late anchor (≤ ANCHOR_TIMEOUT_MS
        // settle) still reopens the spotlight.
        handoffDone = true;
        setTargetEl(null);
      }
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
    // Keyed on the store's primitive step identity; the step is read live via
    // getTourState() inside, so `state` itself is not a dependency.
  }, [state?.activeTourId, state?.stepIndex, location.pathname, isMobile, navigate, setOpen]);

  // Keyboard: arrows navigate, Esc skips, Enter advances (unless a control is
  // focused, so it doesn't double-fire with a button's own activation).
  useEffect(() => {
    if (!active) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      const tag = document.activeElement?.tagName;
      const inTextControl = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
      if (event.key === 'Escape') {
        event.preventDefault();
        stopTour('dismissed');
      } else if (event.key === 'ArrowRight' && !inTextControl) {
        event.preventDefault();
        next();
      } else if (event.key === 'ArrowLeft' && !inTextControl) {
        event.preventDefault();
        back();
      } else if (event.key === 'Enter') {
        if (tag === 'BUTTON' || tag === 'A' || inTextControl) return;
        event.preventDefault();
        next();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [active, stopTour, next, back]);

  const value = useMemo<TourContextValue>(
    () => ({
      active,
      step: currentStep,
      stepIndex: state?.stepIndex ?? -1,
      stepCount,
      startTour,
      stopTour,
      next,
      back,
    }),
    [active, currentStep, state?.stepIndex, stepCount, startTour, stopTour, next, back]
  );

  return (
    <TourContext.Provider value={value}>
      {children}
      {active && currentStep && state && (
        <TourOverlay
          step={currentStep}
          stepIndex={state.stepIndex}
          stepCount={stepCount}
          targetEl={targetEl}
          isMobile={isMobile}
          onNext={next}
          onBack={back}
          onSkip={() => stopTour('dismissed')}
        />
      )}
    </TourContext.Provider>
  );
}
